import React, { useEffect, useMemo, useRef, useState } from "react";
import { FileQuestion, X } from "lucide-react";
import Button from "./ui/Button";
import ScrollableContainer from "./ui/ScrollableContainer";
import { useFocusTrap } from "../hooks/useFocusTrap";
import {
  getUrdfDisplayPath,
  orderUrdfCandidates,
  URDF_IMPORT_CHOICE_EVENT,
} from "../utils/urdfImportChoice";

export default function UrdfImportChooserModal() {
  const [request, setRequest] = useState(null);
  const dialogRef = useRef(null);
  useFocusTrap(dialogRef, Boolean(request));

  useEffect(() => {
    const handleChoiceRequest = (event) => {
      const candidates = Array.isArray(event.detail?.candidates) ? event.detail.candidates : [];
      const resolve = typeof event.detail?.resolve === "function" ? event.detail.resolve : null;
      if (!resolve || candidates.length === 0) return;
      setRequest({ candidates, resolve });
    };
    window.addEventListener(URDF_IMPORT_CHOICE_EVENT, handleChoiceRequest);
    return () => window.removeEventListener(URDF_IMPORT_CHOICE_EVENT, handleChoiceRequest);
  }, []);

  const orderedCandidates = useMemo(
    () => (request ? orderUrdfCandidates(request.candidates) : []),
    [request]
  );

  useEffect(() => {
    if (!request) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      request.resolve(null);
      setRequest(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [request]);

  if (!request) return null;

  const close = () => {
    request.resolve(null);
    setRequest(null);
  };

  const selectCandidate = (file) => {
    request.resolve(file);
    setRequest(null);
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="urdf-import-chooser-title"
        className="flex max-h-[min(520px,calc(100vh-32px))] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-gray-700 bg-[rgba(28,28,30,0.98)] text-gray-100 shadow-2xl"
      >
        <div className="border-b border-gray-700/80 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-md border border-sky-400/60 bg-sky-500/10 p-2 text-sky-300 shadow-[0_0_0_1px_rgba(56,189,248,0.12)]">
              <FileQuestion className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Folder import
              </div>
              <h2 id="urdf-import-chooser-title" className="mt-0.5 text-base font-semibold">
                Choose a model to import
              </h2>
              <p className="mt-1 text-sm text-gray-300">
                Select the file Critter should load from this folder.
              </p>
            </div>
            <Button
              variant="ghost"
              size="small"
              className="shrink-0 px-2 py-1 text-xs text-gray-300 hover:text-gray-100"
              onClick={close}
              title="Cancel import"
              aria-label="Cancel import"
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>

        <ScrollableContainer className="min-h-0 flex-1 p-3">
          <div className="space-y-1">
            {orderedCandidates.map((file, index) => {
              const displayPath = getUrdfDisplayPath(file, request.candidates);
              return (
                <button
                  key={`${displayPath}-${index}`}
                  type="button"
                  onClick={() => selectCandidate(file)}
                  className="w-full rounded border border-[rgba(95,95,95,0.4)] bg-[rgba(48,48,48,0.92)] px-3 py-2 text-left text-sm text-neutral-100 transition-colors hover:border-gray-600 hover:bg-gray-700/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500"
                >
                  <span className="block truncate font-mono text-xs">{displayPath}</span>
                </button>
              );
            })}
          </div>
        </ScrollableContainer>

        <div className="flex justify-end gap-2 border-t border-gray-700/80 px-4 py-3">
          <Button variant="ghost" size="small" onClick={close}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
